import { css } from "@emotion/react";
import { DimensionBreakdownComponent } from "@src/components/ab-test/dimension-breakdown-component";
import { Button, Collapse, Divider, Form, InputNumber, Tabs, Typography } from "antd";
import { FormInstance } from "antd/lib";
import { SignificanceLevelMemo } from "../metric-performance/component/metric-performance-item/significance-level";
import { ChartFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import React, { useEffect } from "react";
import { RetentionShowWrapper } from "./retention-show-wrapper";

export const RetentionExploration: React.FC<{ form: FormInstance<any> }> = ({ form }) => {
    const [_, setTriggerRender] = React.useState(new Date().getTime());
    const [activeKey, setActiveKey] = React.useState<string[]>([]);

    const previousApplyValue = React.useRef<{
        breakdownBy: string;
        breakdownValue: string[];
        significant: number;
    } | null>(null);
    const [applyValue, setApplyValue] = React.useState<{
        breakdownBy: string;
        breakdownValue: string[];
        significant: number;
    } | null>(null);
    const applyButtonRef = React.useRef<HTMLButtonElement>(null);

    const retentionBreakdown = Form.useWatch(ChartFormFieldsEnum.RETENTION_BREAKDOWN, form);
    const retentionBreakdownValue = Form.useWatch(ChartFormFieldsEnum.RETENTION_BREAKDOWN_VALUE, form);
    const retentionSignificant = Form.useWatch(ChartFormFieldsEnum.RETENTION_SIGNIFICANT, form);

    const configRealtime = React.useMemo(() => {
        return [retentionBreakdown, retentionBreakdownValue, retentionSignificant];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify([retentionBreakdown, retentionBreakdownValue, retentionSignificant])]);

    const isDisabledApply = React.useMemo(() => {
        if (!applyValue) return false;
        return (
            JSON.stringify([applyValue.breakdownBy, applyValue.breakdownValue, applyValue.significant]) ===
            JSON.stringify(configRealtime)
        );
    }, [applyValue, configRealtime]);
    const isReadyInitialValues =
        activeKey.length > 0 && form.getFieldValue(ChartFormFieldsEnum.FILTER_FIELDS) !== undefined;

    const intervalId = React.useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        if (activeKey.length === 0) return;
        if (isReadyInitialValues && applyButtonRef.current) {
            applyButtonRef.current?.click();
            return () => {
                if (intervalId.current) {
                    clearInterval(intervalId.current);
                }
            };
        }
        intervalId.current = setInterval(() => {
            setTriggerRender(new Date().getTime());
        }, 300);
        return () => {
            if (intervalId.current) {
                clearInterval(intervalId.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReadyInitialValues, applyButtonRef.current, activeKey]);

    return (
        <>
            <Collapse
                activeKey={activeKey}
                onChange={(key) => {
                    setActiveKey(key);
                }}
                className="ab-testing-collapse mb-4"
                items={[
                    {
                        key: "retention-exploration",
                        label: <Typography.Text strong>Retention</Typography.Text>,
                        children: (
                            <div>
                                <div className="flex gap-2 flex-wrap">
                                    <div className="flex-1">
                                        <DimensionBreakdownComponent
                                            isReadyInitialValues={isReadyInitialValues}
                                            initialValues={
                                                isReadyInitialValues
                                                    ? {
                                                          breakdownBy: form.getFieldValue(
                                                              ChartFormFieldsEnum.RETENTION_BREAKDOWN,
                                                          ),
                                                          breakdownValue: form.getFieldValue(
                                                              ChartFormFieldsEnum.RETENTION_BREAKDOWN_VALUE,
                                                          ),
                                                      }
                                                    : undefined
                                            }
                                            chartForm={form}
                                            onChange={(values) => {
                                                form.setFieldsValue({
                                                    [ChartFormFieldsEnum.RETENTION_BREAKDOWN]: values.breakdownBy,
                                                    [ChartFormFieldsEnum.RETENTION_BREAKDOWN_VALUE]:
                                                        values.breakdownValue,
                                                });
                                            }}
                                        />
                                    </div>

                                    <div
                                        className="mr-8"
                                        css={css`
                                            .ant-form-item {
                                                margin: 0 !important;
                                            }
                                        `}
                                    >
                                        <SignificanceLevelMemo
                                            form={form}
                                            fieldName={ChartFormFieldsEnum.RETENTION_SIGNIFICANT}
                                            _onChange={(value) => {
                                                form.setFieldValue(ChartFormFieldsEnum.RETENTION_SIGNIFICANT, value);
                                            }}
                                        />
                                    </div>
                                    <Button
                                        ref={applyButtonRef}
                                        type="primary"
                                        disabled={isDisabledApply}
                                        onClick={() => {
                                            const newApplyValue = {
                                                breakdownBy: form.getFieldValue(
                                                    ChartFormFieldsEnum.RETENTION_BREAKDOWN,
                                                ),
                                                breakdownValue: form.getFieldValue(
                                                    ChartFormFieldsEnum.RETENTION_BREAKDOWN_VALUE,
                                                ),
                                                significant: form.getFieldValue(
                                                    ChartFormFieldsEnum.RETENTION_SIGNIFICANT,
                                                ),
                                            };
                                            previousApplyValue.current = newApplyValue;
                                            setTimeout(() => {
                                                setApplyValue(newApplyValue);
                                            }, 0);
                                        }}
                                    >
                                        Apply
                                    </Button>
                                </div>

                                <Divider className="m-0 mt-4" />
                                <div
                                    css={css`
                                        td.ant-table-row-expand-icon-cell {
                                            background-color: #e9f6fe !important;
                                        }
                                        td {
                                            &:has(.table-group-custom) {
                                                background-color: #e9f6fe !important;
                                            }
                                        }
                                    `}
                                >
                                    {applyValue && (
                                        <Tabs
                                            type="card"
                                            items={[
                                                {
                                                    key: "retention",
                                                    label: "Retention",
                                                    children: (
                                                        <RetentionShowWrapper
                                                            form={form}
                                                            applyValue={applyValue}
                                                            metricType={"retention"}
                                                        />
                                                    ),
                                                },
                                                {
                                                    key: "sum_retention",
                                                    label: "Sum Retention",
                                                    children: (
                                                        <RetentionShowWrapper
                                                            form={form}
                                                            applyValue={applyValue}
                                                            metricType={"sum_retention"}
                                                        />
                                                    ),
                                                },
                                            ]}
                                        />
                                    )}
                                </div>
                            </div>
                        ),
                    },
                ]}
            />
            <Form.Item name={ChartFormFieldsEnum.RETENTION_BREAKDOWN} hidden>
                <InputNumber />
            </Form.Item>
            <Form.Item name={ChartFormFieldsEnum.RETENTION_BREAKDOWN_VALUE} hidden>
                <InputNumber />
            </Form.Item>
            <Form.Item name={ChartFormFieldsEnum.RETENTION_SIGNIFICANT} hidden>
                <InputNumber />
            </Form.Item>
        </>
    );
};
